import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBell,
    faBars,
    faAnglesLeft,
    faAnglesRight,
    faUserCircle,
    faChevronDown,
    faRightFromBracket,
    faMoon,
    faSun,
    faCircleHalfStroke,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * Top bar of the app shell. Left side holds the panel show/hide controls (an
 * off-canvas toggle on mobile and a collapse toggle on desktop). Right side
 * holds the notifications bell (not implemented yet, shows an empty state) and
 * the account button, which opens a dropdown with the current user and a
 * logout action.
 */
export function Topbar({ onToggleSidebar, onToggleCollapse, collapsed }) {
    const { user, logout } = useAuth();
    const { preference, theme, cycle } = useTheme();
    const navigate = useNavigate();
    const [accountOpen, setAccountOpen] = useState(false);
    const [notifOpen, setNotifOpen] = useState(false);
    const actionsRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (actionsRef.current && !actionsRef.current.contains(e.target)) {
                setAccountOpen(false);
                setNotifOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const email = user?.email || user?.username || 'Usuario';

    const themeMeta = {
        dark: { icon: faMoon, label: 'Oscuro', next: 'claro' },
        light: { icon: faSun, label: 'Claro', next: 'automático' },
        system: { icon: faCircleHalfStroke, label: `Automático (${theme === 'dark' ? 'oscuro' : 'claro'})`, next: 'oscuro' },
    }[preference] || { icon: faMoon, label: 'Oscuro', next: 'claro' };

    const handleLogout = () => {
        setAccountOpen(false);
        logout();
        navigate('/login');
    };

    return (
        <header className="app-topbar">
            <button
                type="button"
                className="app-icon-btn app-menu-toggle"
                onClick={onToggleSidebar}
                aria-label="Abrir menú de navegación"
            >
                <FontAwesomeIcon icon={faBars} />
            </button>

            <button
                type="button"
                className="app-icon-btn app-collapse-toggle"
                onClick={onToggleCollapse}
                aria-label={collapsed ? 'Mostrar panel' : 'Ocultar panel'}
                title={collapsed ? 'Mostrar panel' : 'Ocultar panel'}
            >
                <FontAwesomeIcon icon={collapsed ? faAnglesRight : faAnglesLeft} />
            </button>

            <div className="app-topbar-spacer" />

            <div className="app-topbar-actions" ref={actionsRef}>
                <div className="app-topbar-item">
                    <button
                        type="button"
                        className="app-icon-btn"
                        onClick={cycle}
                        aria-label={`Tema: ${themeMeta.label}`}
                        title={`Tema: ${themeMeta.label} · clic para ${themeMeta.next}`}
                    >
                        <FontAwesomeIcon icon={themeMeta.icon} />
                    </button>
                </div>

                <div className="app-topbar-item">
                    <button
                        type="button"
                        className="app-icon-btn"
                        aria-label="Notificaciones"
                        title="Notificaciones"
                        onClick={() => {
                            setNotifOpen((v) => !v);
                            setAccountOpen(false);
                        }}
                    >
                        <FontAwesomeIcon icon={faBell} />
                    </button>
                    {notifOpen && (
                        <div className="app-dropdown app-notif-menu">
                            <div className="app-dropdown-empty">
                                Aún no tienes notificaciones.
                            </div>
                        </div>
                    )}
                </div>

                <div className="app-topbar-item">
                    <button
                        type="button"
                        className="app-account-btn"
                        onClick={() => {
                            setAccountOpen((v) => !v);
                            setNotifOpen(false);
                        }}
                    >
                        <FontAwesomeIcon icon={faUserCircle} className="app-account-icon" />
                        <span className="app-account-email">{email}</span>
                        <FontAwesomeIcon icon={faChevronDown} className="app-account-caret" />
                    </button>
                    {accountOpen && (
                        <div className="app-dropdown app-account-menu">
                            <div className="app-dropdown-user">
                                <FontAwesomeIcon icon={faUserCircle} className="app-dropdown-user-icon" />
                                <span className="app-dropdown-user-email">{email}</span>
                            </div>
                            <button
                                type="button"
                                className="app-dropdown-item danger"
                                onClick={handleLogout}
                            >
                                <FontAwesomeIcon icon={faRightFromBracket} />
                                <span>Cerrar sesión</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
}

export default Topbar;
